// Pohon aktif disimpan di state aplikasi (+ localStorage); semua layar per pohon membacanya dari sini.
import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { api, type ResponseOf } from './api';

export type TreeItem = ResponseOf<'trees'>[number];

const STORAGE_KEY = 'fp_active_tree';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

interface ActiveTreeContext {
  trees: TreeItem[];
  activeTree: TreeItem | undefined;
  setActiveTreeId: (id: string) => void;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

const Context = createContext<ActiveTreeContext | null>(null);

export function ActiveTreeProvider({ children }: { children: ReactNode }) {
  const query = useQuery({ queryKey: ['trees'], queryFn: () => api('trees') });
  const [selected, setSelected] = useState<string | null>(readStored);

  const setActiveTreeId = useCallback((id: string) => {
    setSelected(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Penyimpanan diblokir: pilihan tetap berlaku selama sesi ini.
    }
  }, []);

  const value = useMemo<ActiveTreeContext>(() => {
    const trees = query.data ?? [];
    const activeTree =
      trees.find((t) => t.id === selected) ??
      trees.find((t) => t.isDefault && t.status === 'ACTIVE') ??
      trees.find((t) => t.status === 'ACTIVE') ??
      trees[0];
    return {
      trees,
      activeTree,
      setActiveTreeId,
      isLoading: query.isPending,
      error: query.error,
      refetch: () => void query.refetch(),
    };
  }, [query, selected, setActiveTreeId]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useActiveTree(): ActiveTreeContext {
  const context = useContext(Context);
  if (!context) throw new Error('useActiveTree di luar ActiveTreeProvider');
  return context;
}
